import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Save, Loader2, ShieldCheck } from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.35 · Configuración global de caducidad de enlaces.
// Todos los enlaces (Alta Digital, Firmas, DGT, Disponibilidad, Documentos)
// caducan a las X horas (default 12). El admin puede cambiarlo aquí.
//
// Presets sugeridos: 12h · 24h · 48h · personalizado.
// Máximo permitido: 720h (30 días).

const PRESETS = [
  { hours: 12, label: "12 horas", tag: "Por defecto (V1.35)" },
  { hours: 24, label: "24 horas", tag: "" },
  { hours: 48, label: "48 horas", tag: "" },
];

export default function LinkTtlSettings() {
  const pin = usePinSecurity();
  const [state, setState] = useState({ default_hours: 12, max_hours: 720, presets: [12, 24, 48] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(12);
  const [customHours, setCustomHours] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/links/ttl");
      setState(data);
      const isPreset = (PRESETS.map(p => p.hours)).includes(data.default_hours);
      setSelected(isPreset ? data.default_hours : "custom");
      if (!isPreset) setCustomHours(String(data.default_hours));
    } catch (err) {
      console.warn("link-ttl load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    let hours;
    if (selected === "custom") {
      const n = parseInt(customHours, 10);
      if (!Number.isFinite(n) || n < 1 || n > state.max_hours) {
        toast.error(`Debe ser un número entre 1 y ${state.max_hours} horas`);
        return;
      }
      hours = n;
    } else {
      hours = parseInt(selected, 10);
    }
    setSaving(true);
    try {
      await pin.require("setting.update");
      const { data } = await api.patch("/settings/links/ttl", { hours });
      setState((s) => ({ ...s, default_hours: data.default_hours }));
      toast.success(`✅ TTL actualizado: ${hours} h`);
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(m)) toast.error(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="link-ttl-settings">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-8 w-8 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center">
          <Clock className="h-4 w-4" />
        </span>
        <div>
          <h3 className="font-semibold text-base leading-tight">
            Caducidad de enlaces (V1.35)
          </h3>
          <p className="text-xs text-neutral-500 leading-snug mt-0.5">
            Todos los enlaces (Alta Digital · Firmas · DGT · Disponibilidad · Documentos) caducan al
            expirar el tiempo configurado. Por defecto: <b>12 horas</b>.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.hours}
                type="button"
                onClick={() => setSelected(p.hours)}
                data-testid={`link-ttl-preset-${p.hours}`}
                className={
                  "rounded-xl border p-3 text-left transition-colors " +
                  (selected === p.hours
                    ? "border-orange-500 bg-orange-50"
                    : "border-neutral-200 hover:border-orange-300 bg-white")
                }
              >
                <div className="font-bold text-neutral-900">{p.label}</div>
                {p.tag && <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mt-0.5">{p.tag}</div>}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSelected("custom")}
              data-testid="link-ttl-preset-custom"
              className={
                "rounded-xl border p-3 text-left transition-colors " +
                (selected === "custom"
                  ? "border-orange-500 bg-orange-50"
                  : "border-neutral-200 hover:border-orange-300 bg-white")
              }
            >
              <div className="font-bold text-neutral-900">Personalizada</div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mt-0.5">
                1 – {state.max_hours} h
              </div>
            </button>
          </div>

          {selected === "custom" && (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={state.max_hours}
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                data-testid="link-ttl-custom-input"
                placeholder="Horas"
                className="h-11 w-32 rounded-xl border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <span className="text-sm text-neutral-500">horas</span>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-neutral-500 flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" />
              Actual: <b className="text-neutral-800">{state.default_hours} h</b>
              <span className="mx-1">·</span>
              Máx: {state.max_hours} h
            </div>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="link-ttl-save"
              className="btn-primary !h-10 !px-4 !text-sm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </button>
          </div>
          <p className="text-[11px] text-neutral-400 mt-3 leading-snug">
            El cambio afecta a <b>enlaces nuevos</b>. Los enlaces ya generados
            mantienen su caducidad original. Requiere PIN de administrador.
          </p>
        </>
      )}
    </div>
  );
}
